import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Keyboard, ArrowUp, Square, X, Loader2, ChevronRight, Home, ArrowLeft, RotateCcw } from "lucide-react";
import { useSound } from "@/context/SoundContext";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- shared voice recorder ----
function useRecorder(onText) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const stopStream = () => {
    if (mediaRef.current?.stream) mediaRef.current.stream.getTracks().forEach((t) => t.stop());
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        setState("transcribing");
        try {
          const form = new FormData();
          form.append("audio", new Blob(chunksRef.current, { type: "audio/webm" }), "r.webm");
          const res = await fetch(`${API}/simulation/transcribe`, { method: "POST", body: form });
          const data = await res.json();
          onText((data.text || "").trim());
        } catch (e) {
          onText("");
        }
        setState("idle");
      };
      mr.start();
      setState("recording");
    } catch (e) {
      onText("");
      setState("idle");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
      stopStream();
    }
  };

  return { state, start, stop };
}

// ---- cinematic Speak | Type input ----
const InputBar = ({ onSubmit, disabled }) => {
  const [mode, setMode] = useState(null); // type | speak
  const [text, setText] = useState("");
  const [review, setReview] = useState("");
  const ref = useRef(null);
  const { state, start, stop } = useRecorder((t) => setReview(t || "I'm not sure what to say."));

  useEffect(() => {
    if (mode === "type") setTimeout(() => ref.current?.focus(), 300);
  }, [mode]);

  const reset = () => { setMode(null); setText(""); setReview(""); };
  const sendType = () => { if (text.trim()) { onSubmit(text.trim()); reset(); } };
  const sendVoice = () => { if (review.trim()) { onSubmit(review.trim()); reset(); } };

  if (mode === null) {
    return (
      <div className="flex gap-3" data-testid="input-bar">
        <button
          onClick={() => setMode("speak")}
          disabled={disabled}
          data-testid="action-speak"
          className="flex flex-1 items-center justify-center gap-2.5 rounded-full border border-white/12 bg-white/[0.05] px-5 py-4 text-base font-medium text-white/85 backdrop-blur-xl transition-colors duration-300 hover:border-[#4FA3D9]/60 hover:text-white active:scale-[0.98]"
        >
          <Mic size={16} style={{ color: ACCENT }} /> Speak
        </button>
        <button
          onClick={() => setMode("type")}
          disabled={disabled}
          data-testid="action-type"
          className="flex flex-1 items-center justify-center gap-2.5 rounded-full border border-white/12 bg-white/[0.05] px-5 py-4 text-base font-medium text-white/85 backdrop-blur-xl transition-colors duration-300 hover:border-[#4FA3D9]/60 hover:text-white active:scale-[0.98]"
        >
          <Keyboard size={16} style={{ color: ACCENT }} /> Type
        </button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-3xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-xl"
      data-testid={mode === "type" ? "type-panel" : "speak-panel"}
    >
      <button onClick={reset} data-testid="panel-close" className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-white/40 hover:bg-white/10 hover:text-white/80">
        <X size={15} />
      </button>

      {mode === "type" ? (
        <div className="flex items-end gap-3 pr-8">
          <textarea
            ref={ref}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendType(); } }}
            rows={1}
            placeholder="Say it in your own words…"
            data-testid="type-input"
            className="max-h-32 min-h-[40px] flex-1 resize-none bg-transparent text-base text-white placeholder:text-white/35 focus:outline-none"
          />
          <button onClick={sendType} disabled={!text.trim()} data-testid="type-submit" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-opacity disabled:opacity-30" style={{ background: ACCENT }}>
            <ArrowUp size={18} className="text-white" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center py-2 pr-6 text-center">
          {review ? (
            <>
              <p className="mb-4 max-w-md text-base leading-relaxed text-white/85" data-testid="voice-transcript">&ldquo;{review}&rdquo;</p>
              <div className="flex gap-3">
                <button onClick={() => setReview("")} data-testid="voice-retry" className="rounded-full border border-white/15 px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-white/70 hover:bg-white/5">Try again</button>
                <button onClick={sendVoice} data-testid="voice-submit" className="rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-white" style={{ background: ACCENT }}>Send</button>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={state === "recording" ? stop : start}
                disabled={state === "transcribing"}
                data-testid="mic-toggle"
                className="relative flex h-16 w-16 items-center justify-center rounded-full transition-transform active:scale-95 disabled:opacity-70"
                style={{ background: state === "recording" ? ACCENT : "rgba(255,255,255,0.06)", border: `1px solid ${state === "recording" ? ACCENT : "rgba(255,255,255,0.14)"}` }}
              >
                {state === "recording" && <span className="absolute inset-0 animate-ping rounded-full" style={{ background: "rgba(79,163,217,0.35)" }} />}
                {state === "transcribing" ? <Loader2 size={22} className="relative animate-spin" style={{ color: ACCENT }} /> : state === "recording" ? <Square size={18} className="relative text-white" fill="white" /> : <Mic size={22} className="relative" style={{ color: ACCENT }} />}
              </button>
              <p className="mt-4 text-sm text-white/60">{state === "recording" ? "Listening… tap to stop" : state === "transcribing" ? "One moment…" : "Tap and say it out loud"}</p>
            </>
          )}
        </div>
      )}
    </motion.div>
  );
};

// ---- messaging chat input ----
const ChatInput = ({ onSubmit, disabled }) => {
  const [text, setText] = useState("");
  const { state, start, stop } = useRecorder((t) => t && setText((p) => (p ? p + " " : "") + t));
  const send = () => { if (text.trim()) { onSubmit(text.trim()); setText(""); } };
  return (
    <div className="flex items-end gap-2 border-t border-white/10 bg-black/40 p-3 backdrop-blur-xl" data-testid="chat-input">
      <button
        onClick={state === "recording" ? stop : start}
        disabled={disabled || state === "transcribing"}
        data-testid="chat-mic"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/12"
        style={{ background: state === "recording" ? ACCENT : "transparent" }}
      >
        {state === "transcribing" ? <Loader2 size={16} className="animate-spin text-white/70" /> : <Mic size={16} style={{ color: state === "recording" ? "#fff" : ACCENT }} />}
      </button>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        rows={1}
        disabled={disabled}
        placeholder="Message…"
        data-testid="chat-text-input"
        className="max-h-24 min-h-[44px] flex-1 resize-none rounded-2xl bg-white/[0.06] px-4 py-3 text-base text-white placeholder:text-white/35 focus:outline-none"
      />
      <button onClick={send} disabled={disabled || !text.trim()} data-testid="chat-send" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-opacity disabled:opacity-30" style={{ background: ACCENT }}>
        <ArrowUp size={17} className="text-white" />
      </button>
    </div>
  );
};

export const SceneScreen = ({ scene, sceneIndex, total, onSceneDone, onHome, onBack }) => {
  const sound = useSound();
  const [turns, setTurns] = useState([]); // {role:'child'|'world', speaker, text}
  const [turnCount, setTurnCount] = useState(0);
  const [reacting, setReacting] = useState(false);
  const [done, setDone] = useState(false);
  const [chosenNpc, setChosenNpc] = useState(scene.npc);
  const [showElse, setShowElse] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    setTurns([]); setTurnCount(0); setReacting(false); setDone(false);
    setChosenNpc(scene.npc); setShowElse(false);
    sound.setAmbientScene({ key: scene.ambient || "calm" });
    if (scene.mode === "messaging" || scene.mode === "actions") {
      const t = setTimeout(() => sound.chime(), 800);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, reacting]);

  const send = useCallback(async (childText, npcOverride) => {
    sound.click();
    const npc = npcOverride || chosenNpc || scene.npc;
    const history = turns.map((t) => ({ role: t.role, text: t.text }));
    const nextCount = turnCount + 1;
    setTurns((p) => [...p, { role: "child", text: childText }]);
    setReacting(true);
    setTurnCount(nextCount);
    try {
      const res = await fetch(`${API}/simulation/react`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scene_id: scene.id, npc, brief: scene.brief, child_text: childText,
          history, turn: nextCount, max_turns: scene.maxTurns || 1,
        }),
      });
      const data = await res.json();
      setTurns((p) => [...p, { role: "world", speaker: data.speaker || npc, text: data.reaction }]);
      if (data.end_scene || nextCount >= (scene.maxTurns || 1)) setDone(true);
    } catch (e) {
      setTurns((p) => [...p, { role: "world", speaker: npc, text: "…" }]);
      setDone(true);
    }
    setReacting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns, turnCount, chosenNpc, scene]);

  const finish = () => {
    sound.click();
    const child = turns.filter((t) => t.role === "child").map((t) => t.text).join(" | ");
    const world = turns.filter((t) => t.role === "world").map((t) => t.text).join(" | ");
    onSceneDone({ scene_title: scene.title, setting: scene.setting, child, world });
  };

  const redo = () => {
    setTurns([]); setTurnCount(0); setReacting(false); setDone(false);
    setChosenNpc(scene.npc); setShowElse(false);
  };

  const chooseAction = (a) => {
    if (a.free) { setShowElse(true); setChosenNpc(a.npc); return; }
    setChosenNpc(a.npc);
    const phrase = {
      reply: "I reply in the group chat.",
      ignore: "I decide to ignore it for now.",
      private: "I message someone I trust privately about it.",
    }[a.id] || a.label;
    send(phrase, a.npc);
  };

  const isMessaging = scene.mode === "messaging";
  const canAct = !reacting && !done;

  // -------------------- MESSAGING (phone UI) --------------------
  if (isMessaging) {
    return (
      <div className="relative h-[100dvh] w-full overflow-hidden bg-[#0a0a0a]" data-testid="scene-screen">
        <img src={scene.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30 blur-sm" />
        <div className="absolute inset-0 bg-[#0a0a0a]/70" />
        <Progress sceneIndex={sceneIndex} total={total} onHome={onHome} onBack={onBack} />

        <div className="relative z-10 mx-auto flex h-full max-w-md flex-col px-4 pb-3 pt-28 sm:pt-32">
          {/* phone header */}
          <div className="flex items-center gap-3 rounded-t-3xl border-x border-t border-white/10 bg-white/[0.06] px-5 py-4 backdrop-blur-xl">
            <div className="flex h-10 w-10 items-center justify-center rounded-full text-base font-semibold text-white" style={{ background: ACCENT }}>{(scene.contact || "M")[0]}</div>
            <div><p className="text-base font-semibold text-white">{scene.contact}</p><p className="text-xs text-white/50">{scene.timestamp}</p></div>
          </div>

          {/* messages */}
          <div ref={scrollRef} className="flex-1 space-y-2.5 overflow-y-auto border-x border-white/10 bg-black/30 px-4 py-5 backdrop-blur-xl">
            {scene.inject.map((m, i) => (
              <Bubble key={`in-${i}`} side="left" delay={i * 0.5}>{m.text}</Bubble>
            ))}
            {turns.map((t, i) => (
              <Bubble key={`t-${i}`} side={t.role === "child" ? "right" : "left"}>{t.text}</Bubble>
            ))}
            {reacting && <Bubble side="left"><Dots /></Bubble>}
          </div>

          {/* input / continue */}
          <div className="rounded-b-3xl border-x border-b border-white/10 bg-black/30 backdrop-blur-xl">
            {done ? (
              <div className="p-3">
                <button onClick={redo} data-testid="redo-btn" className="mb-2 flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-sm font-medium text-white/70 transition-colors hover:text-white">
                  <RotateCcw size={15} /> Try a different reply
                </button>
                <ContinueBar onClick={finish} />
              </div>
            ) : (
              <ChatInput onSubmit={(txt) => send(txt)} disabled={!canAct} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // -------------------- CINEMATIC (converse / actions) --------------------
  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-[#0a0a0a] grain" data-testid="scene-screen">
      <motion.div key={scene.id} className="absolute inset-0" initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}>
        <img src={scene.image} alt="" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/75 to-transparent" />
      <div className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-[#0a0a0a]/80 to-transparent" />
      <Progress sceneIndex={sceneIndex} total={total} timestamp={scene.timestamp} onHome={onHome} onBack={onBack} />

      <div className="absolute inset-x-0 bottom-0 z-10 px-6 pb-8 sm:px-10 sm:pb-12">
        <div className="mx-auto w-full max-w-2xl">
          <div ref={scrollRef} className="mb-6 max-h-[46vh] space-y-4 overflow-y-auto">
            {scene.inject.map((l, i) =>
              l.narration ? (
                <motion.p key={`inj-${i}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.5, duration: 0.9 }} className="text-lg leading-relaxed text-white/75 sm:text-xl" data-testid={`inject-narration-${i}`}>
                  {l.narration}
                </motion.p>
              ) : (
                <motion.div key={`inj-${i}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.5, duration: 0.9 }} data-testid={`inject-dialogue-${i}`}>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>{l.speaker}</p>
                  <p className="border-l-2 pl-4 font-display text-2xl font-medium leading-snug text-white sm:text-3xl" style={{ borderColor: ACCENT }}>&ldquo;{l.text}&rdquo;</p>
                </motion.div>
              )
            )}

            {turns.map((t, i) =>
              t.role === "child" ? (
                <div key={`c-${i}`} className="pt-1 text-right" data-testid={`child-turn-${i}`}>
                  <span className="inline-block max-w-[85%] rounded-2xl bg-white/[0.10] px-4 py-2.5 text-left text-base text-white/90">{t.text}</span>
                </div>
              ) : (
                <div key={`w-${i}`} data-testid={`world-turn-${i}`}>
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>{t.speaker}</p>
                  <p className="border-l-2 pl-4 font-display text-xl font-medium leading-snug text-white sm:text-2xl" style={{ borderColor: ACCENT }}>&ldquo;{t.text}&rdquo;</p>
                </div>
              )
            )}
            {reacting && (
              <div className="flex items-center gap-2 text-base text-white/55" data-testid="reacting-state"><Loader2 size={16} className="animate-spin" style={{ color: ACCENT }} /> …</div>
            )}
          </div>

          {!done && (
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-white/60" data-testid="scene-prompt">{scene.prompt}</p>
          )}

          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="cont" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2.5">
                <button onClick={redo} data-testid="redo-btn" className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-3.5 text-sm font-medium text-white/70 transition-colors hover:text-white">
                  <RotateCcw size={15} /> Try a different response
                </button>
                <ContinueBar onClick={finish} last={scene.last} />
              </motion.div>
            ) : scene.mode === "actions" && !showElse ? (
              <motion.div key="acts" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="action-choices">
                {scene.actions.map((a, i) => (
                  <button key={a.id} onClick={() => chooseAction(a)} disabled={!canAct} data-testid={`action-${a.id}`} className="flex items-center justify-between rounded-2xl border border-white/12 bg-white/[0.04] px-5 py-4 text-left text-base text-white/85 transition-colors duration-300 hover:border-[#4FA3D9]/50 hover:bg-white/[0.07] hover:text-white">
                    {a.label}
                    <ChevronRight size={16} className="text-white/30" />
                  </button>
                ))}
              </motion.div>
            ) : (
              <motion.div key="input" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <InputBar onSubmit={(txt) => send(txt)} disabled={!canAct} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const Progress = ({ sceneIndex, total, timestamp, onHome, onBack }) => (
  <div className="absolute inset-x-0 top-0 z-20 px-6 pt-6 sm:px-8">
    <div className="mb-3 flex items-center gap-2">
      <button onClick={onHome} data-testid="home-btn" aria-label="Home" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/12 bg-black/30 text-white/75 backdrop-blur-xl transition-colors hover:bg-black/50 hover:text-white">
        <Home size={15} />
      </button>
      {onBack && (
        <button onClick={onBack} data-testid="scene-back-btn" aria-label="Back" className="flex h-9 items-center gap-1.5 rounded-full border border-white/12 bg-black/30 px-3 text-white/75 backdrop-blur-xl transition-colors hover:bg-black/50 hover:text-white">
          <ArrowLeft size={15} /> <span className="text-xs font-medium">Back</span>
        </button>
      )}
    </div>
    <div className="mb-3 flex gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="h-[2px] flex-1 overflow-hidden rounded-full bg-white/12">
          <motion.div className="h-full" style={{ background: ACCENT }} initial={{ width: i < sceneIndex ? "100%" : "0%" }} animate={{ width: i <= sceneIndex ? "100%" : "0%" }} transition={{ duration: i === sceneIndex ? 0.8 : 0 }} />
        </div>
      ))}
    </div>
    {timestamp && (
      <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 backdrop-blur-xl" data-testid="scene-timestamp">
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT, boxShadow: `0 0 10px ${ACCENT}` }} />
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/75">{timestamp}</span>
      </div>
    )}
  </div>
);

const Bubble = ({ side, children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.4 }} className={`flex ${side === "right" ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[80%] rounded-2xl px-4 py-3 text-base leading-relaxed sm:text-lg ${side === "right" ? "rounded-br-md text-white" : "rounded-bl-md bg-white/[0.10] text-white"}`}
      style={side === "right" ? { background: ACCENT } : undefined}
    >
      {children}
    </div>
  </motion.div>
);

const Dots = () => (
  <span className="flex gap-1 py-1">
    {[0, 1, 2].map((i) => (
      <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-white/60" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }} />
    ))}
  </span>
);

const ContinueBar = ({ onClick, last }) => (
  <button
    onClick={onClick}
    data-testid="continue-btn"
    className="group flex w-full items-center justify-center gap-3 rounded-full px-7 py-4 text-base font-semibold uppercase tracking-[0.16em] text-white transition-transform active:scale-[0.98]"
    style={{ background: ACCENT }}
  >
    {last ? "End simulation" : "Continue"}
    <ChevronRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
  </button>
);

export default SceneScreen;
